import "pannellum/src/js/pannellum";
import "pannellum/src/js/libpannellum";
import "pannellum/src/css/pannellum.css";
import { Tooltip as BootstrapTooltip } from "bootstrap";

import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ImageFrame from "../../models/imageFrame";
import ImagePreloader from "../../models/imagePreloader";
import Navigable, { NavigatorMove } from "../../models/navigable";
import Navigator from "../../models/navigator";
import NavigatorPosition from "../../models/navigatorPosition";
import LogService from "../../services/logService";
import LogDirectory from "../../services/logDirectory";
import imageService from "../../services/imageService";
import ImageCacheItem from "../../models/imageCacheItem";
import ProjectInfo from "../../models/projectInfo";
import ImageNotAvailableImage from "../../assets/images/image-not-available-wide.png";
import ImageNotAvailableImageEmptyWhite from "../../assets/images/image-not-available-empty-white.png";
import ImageHd from "../../assets/images/image-hd.png";
import Tooltip from "../common/Tooltip";
import imageHelper from "../../util/imageHelper";
import featureService from "../../services/featureService";
import Feature from "../../models/feature";
import OverlayShowHide, {
  OverlayShowHideRef,
} from "../common/overlays/OverlayShowHide";

const logService = new LogService(LogDirectory.EnableCameraLogging);

declare var pannellum: any;

export interface CameraPanoRef {
  refresh: () => void;
}

interface Props {
  cameraName: string;
  navigator: Navigator;
  project: ProjectInfo;
  setPlaybackElement: any;
  restorePlaybackElement: any;
  //children: React.ReactNode;
}

const CameraPano = forwardRef((props: Props, ref: Ref<CameraPanoRef>) => {
  const {
    cameraName,
    navigator,
    project,
    setPlaybackElement,
    restorePlaybackElement,
  } = props;

  useImperativeHandle(ref, () => ({ refresh }));

  const overlayShowHideRef = useRef<OverlayShowHideRef>(null);

  const [debugMessage, setDebugMessage] = useState<string>("Debug");

  const [imageNotAvailable, setImageNotAvailable] = useState<boolean>(false);

  const [refreshCount, setRefreshCount] = useState(0);

  const [isLoadFullSizeImage, setIsLoadFullSizeImage] =
    useState<boolean>(false);

  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const [navigable, setNavigable] = useState<Navigable>();

  const getCameraId = (cameraName: string) => {
    return `panorama${cameraName}`;
  };

  useEffect(() => {
    // console.log("panoCamInit()");

    let isLoadFullSizeImageInternal: boolean = false;

    if (
      featureService.isFeatureEnabled(
        Feature.AutoProgressiveImageLoading,
        project,
      )
    ) {
      isLoadFullSizeImageInternal = true;
    }

    const createViewer = () => {
      let _viewer = pannellum.viewer(getCameraId(cameraName), {
        default: {
          firstScene: "imageId",
          hfov: 120,
          author: refreshCount.toString(),
        },
        scenes: {
          sceneId: {},
        },
      });
      return _viewer;
    };

    let viewer = createViewer();
    let canvas: any;

    let isFullScreenTooltipsSet = false;

    viewer.on("fullscreenchange", (fullscreenActive: boolean) => {
      // console.log(
      //   "penelum-fullscreenchange-fullscreenActive",
      //   fullscreenActive,
      // );

      setIsFullScreen(fullscreenActive);

      if (fullscreenActive) {
        if (!isFullScreenTooltipsSet) {
          isFullScreenTooltipsSet = true;
          initTooltips(true);
        }

        loadFullSizeImage();
      } else {
        restorePlaybackElement();
      }
    });

    const loadFullSizeImage = () => {
      // console.trace();

      if (currentBatchImageFrameIndex === -1) {
        return;
      }

      const imageUrlToLoad =
        currentBatchImageFrames[currentBatchImageFrameIndex].imageFullSizeUrl;

      setTimeout(() => {
        const currentImageUrlBeforeLoad =
          currentBatchImageFrames[currentBatchImageFrameIndex].imageFullSizeUrl;

        if (imageUrlToLoad !== currentImageUrlBeforeLoad) {
          setDebugMessage(`[${cameraName}] FullSize image load abort.`);
          return;
        }

        setDebugMessage(`[${cameraName}] FullSize image loading...`);

        imageHelper
          .loadImage(imageUrlToLoad)
          .then((image) => {
            setDebugMessage(`[${cameraName}] FullSize image loading done.`);

            const currentImageUrlAfterLoad =
              currentBatchImageFrames[currentBatchImageFrameIndex]
                .imageFullSizeUrl;

            if (imageUrlToLoad !== currentImageUrlAfterLoad) {
              setDebugMessage(`[${cameraName}] FullSize image set abort.`);
              return;
            }

            setDebugMessage(`[${cameraName}] FullSize image set.`);
            loadSceneImage(image.src);
          })
          .catch((error) => {
            setDebugMessage(
              `[${cameraName}] FullSize image load error ${error}`,
            );
          });
      }, 100);
    };

    logService.log(`camera[${cameraName}]:init()`);

    const imagePreloader: ImagePreloader = new ImagePreloader();

    let currentIdSession: number | null = null;
    let currentBatchImageFrames: ImageFrame[] = [];
    let currentBatchImageFrameIndex: number = -1;

    const navigable = new Navigable();
    navigable.id = `camera[${cameraName}]`;
    setNavigable(navigable);

    navigable.function1 = () => {
      // console.log(`camera[${cameraName}]:navigable.function1()`);
      isLoadFullSizeImageInternal = true;

      if (isLoadFullSizeImageInternal && !navigator.isPlaying) {
        loadFullSizeImage();
      }
    };

    let noImages: boolean = false;

    const loadData = async (navigatorPosition: NavigatorPosition) => {
      logService.log(`camera[${cameraName}]:loadData()`);
      try {
        const { data: sessionImageFrameIndex } =
          await imageService.getImageFrameIndex(
            project.idProject,
            navigatorPosition.idSession,
            navigatorPosition.distance,
            cameraName,
          );

        let startIndex =
          sessionImageFrameIndex - imageService.preloaderBackCacheSize * 10;
        if (startIndex < 0) {
          startIndex = 0;
        }
        const stopIndex = startIndex + imageService.batchSize;

        const { data: frames } = await imageService.getImageFrames(
          project.idProject,
          navigatorPosition.idSession,
          cameraName,
          startIndex,
          stopIndex,
        );

        currentBatchImageFrames = frames;

        currentBatchImageFrameIndex =
          NavigatorPosition.getClosestNavigatorPositionIndex(
            navigatorPosition,
            currentBatchImageFrames,
          );

        setImageNotAvailable(false);

        noImages = frames.length === 0;

        if (noImages) {
          setImageNotAvailable(true);

          viewer.addScene("imageId", {
            panorama: ImageNotAvailableImageEmptyWhite,
            autoLoad: true,
          });
          viewer.loadScene("imageId");

          const renderer = viewer.getRenderer();
          if (renderer) {
            canvas = renderer.getCanvas();
            removeWebglContextHooks();
            addWebglContextHooks();
          }

          return;
        }

        var imageUrls = [];
        for (const frame of frames) {
          imageUrls.push(frame.imageUrl);
        }

        imagePreloader.setCameraName(cameraName);
        imagePreloader.setImageUrls(imageUrls);

        logService.log(`camera[${cameraName}]:loadData()-done`);
      } catch (error) {
        logService.log(error);
      }
    };

    const loadSceneImage = (imageUrl: string) => {
      //setImageUrl(imageUrl);
      // const removeSceneResult = viewer.removeScene("imageId");
      // console.log("removeSceneResult", removeSceneResult);

      viewer.addScene("imageId", {
        type: "equirectangular",
        panorama: `${imageUrl}`,
        autoLoad: true,
        hfov: viewer.getHfov(),
        yaw: viewer.getYaw(),
        pitch: viewer.getPitch(),
      });
      viewer.loadScene("imageId");

      const renderer = viewer.getRenderer();
      if (renderer) {
        canvas = renderer.getCanvas();
        removeWebglContextHooks();
        addWebglContextHooks();
      }
    };

    const navigatorMove: NavigatorMove = async (
      navigatorPosition: NavigatorPosition,
    ) => {
      // console.log(`camera[${cameraName}]:navigator:move()`);

      if (currentIdSession === navigatorPosition.idSession) {
        currentBatchImageFrameIndex =
          NavigatorPosition.getClosestNavigatorPositionIndex(
            navigatorPosition,
            currentBatchImageFrames,
          );
      } else {
        currentBatchImageFrameIndex = -1;
        currentIdSession = navigatorPosition.idSession;
        noImages = false;
      }

      if (currentBatchImageFrameIndex === -1 && !noImages) {
        await loadData(navigatorPosition);
      }

      if (noImages) {
        return;
      }

      const imageUrl = await imagePreloader.getImage(
        currentBatchImageFrameIndex,
      );
      if (imageUrl && imageUrl !== ImageCacheItem.LOADING) {
        logService.log(`camera[${cameraName}]:navigator:imageUrl`, imageUrl);
        loadSceneImage(imageUrl);

        if (isLoadFullSizeImageInternal && !navigator.isPlaying) {
          loadFullSizeImage();
        }
      }

      logService.log(`camera[${cameraName}]:navigator:move()-done`);
    };
    navigable.navigatorMove = navigatorMove;

    const playbackIsPlayingChanged = (isPlaying: boolean) => {
      if (isLoadFullSizeImageInternal && !isPlaying) {
        loadFullSizeImage();
      }
    };
    navigable.playbackIsPlayingChanged = playbackIsPlayingChanged;

    const addWebglContextHooks = () => {
      if (!canvas) {
        return;
      }

      canvas.addEventListener("webglcontextlost", onWebglContextLost, false);

      canvas.addEventListener(
        "webglcontextrestored",
        onWebglContextRestored,
        false,
      );
    };

    const removeWebglContextHooks = () => {
      if (!canvas) {
        return;
      }

      canvas.removeEventListener("webglcontextlost", onWebglContextLost, false);

      canvas.removeEventListener(
        "webglcontextrestored",
        onWebglContextRestored,
        false,
      );
    };

    const onWebglContextLost = (event: any) => {
      console.log("Pano Camera Webgl Context Lost");
      event.preventDefault();
    };

    const onWebglContextRestored = (event: any) => {
      console.log("Pano Camera Webgl Context Restored");
      event.preventDefault();

      viewer.destroy();
      viewer = createViewer();

      // refresh
      if (navigator.navigatorPosition) {
        navigatorMove(navigator.navigatorPosition);
      }
    };

    navigator.attach(navigable);

    // this sould be called on open/close window
    if (
      navigator.navigatorPosition &&
      navigator.navigatorPosition.idProject === project.idProject
    ) {
      // console.log(`camera[${cameraName}]:navigator:move()-init`);
      navigatorMove(navigator.navigatorPosition);
    }

    return () => {
      navigable.function1 = undefined;
      navigator.detach(navigable);
      imagePreloader.clearCache();
      removeWebglContextHooks();
      viewer.destroy();
    };
  }, [cameraName, navigator, refreshCount, project, restorePlaybackElement]);

  useEffect(() => {
    initTooltips(false);
  }, [refreshCount]);

  const initTooltips = (isFullScreen: boolean) => {
    if (!containerRef.current) {
      return;
    }

    // console.log(`cameraPano-initTooltips(${isFullScreen})`);

    // zoom in button

    Array.from(
      containerRef.current.querySelectorAll(
        'div[class="pnlm-zoom-in pnlm-sprite pnlm-control"]',
      ),
    ).forEach(
      (tooltipNode) =>
        new BootstrapTooltip(tooltipNode, {
          trigger: "hover",
          placement: "right",
          title: "Zoom in",
          container:
            isFullScreen && document.fullscreenElement
              ? document.fullscreenElement
              : "body",
        }),
    );

    // zoom out button

    Array.from(
      containerRef.current.querySelectorAll(
        'div[class="pnlm-zoom-out pnlm-sprite pnlm-control"]',
      ),
    ).forEach(
      (tooltipNode) =>
        new BootstrapTooltip(tooltipNode, {
          trigger: "hover",
          placement: "right",
          title: "Zoom out",
          container:
            isFullScreen && document.fullscreenElement
              ? document.fullscreenElement
              : "body",
        }),
    );

    // enter full screen button - normal

    Array.from(
      containerRef.current.querySelectorAll(
        'div[class="pnlm-fullscreen-toggle-button pnlm-sprite pnlm-fullscreen-toggle-button-inactive pnlm-controls pnlm-control"]',
      ),
    ).forEach(
      (tooltipNode) =>
        new BootstrapTooltip(tooltipNode, {
          trigger: "hover",
          placement: "right",
          title: "Full Screen",
          container:
            isFullScreen && document.fullscreenElement
              ? document.fullscreenElement
              : "body",
        }),
    );

    // enter full screen button - in full screen

    Array.from(
      containerRef.current.querySelectorAll(
        'div[class="pnlm-fullscreen-toggle-button pnlm-sprite pnlm-fullscreen-toggle-button-inactive pnlm-controls pnlm-control pnlm-fullscreen-toggle-button-active"]',
      ),
    ).forEach(
      (tooltipNode) =>
        new BootstrapTooltip(tooltipNode, {
          trigger: "hover",
          placement: "right",
          title: "Full Screen",
          container:
            isFullScreen && document.fullscreenElement
              ? document.fullscreenElement
              : "body",
        }),
    );
  };

  const refresh = () => {
    // console.log("panoCamRefresh()");
    setRefreshCount((prev) => prev + 1);
  };

  const onHdImageLoad = async () => {
    setIsLoadFullSizeImage(true);

    navigable?.function1();
  };

  const onMouseMove = (event: any) => {
    overlayShowHideRef.current?.onMouseMove();
  };

  return (
    <div className="camera-img-container" ref={containerRef}>
      <div style={{ display: "none" }}>{debugMessage}</div>
      <div
        className="d-flex flex-column"
        style={{ position: "absolute", top: "4px", left: "4px" }}
      >
        {/* vertical space for pano camera build-in buttons */}
        <div style={{ height: "90px" }} />

        {!featureService.isFeatureEnabled(
          Feature.AutoProgressiveImageLoading,
          project,
        ) && (
          <Tooltip
            title="Hi-Res"
            placement="right"
            parentContainer={containerRef}
          >
            <div
              className={
                "image-button" +
                (isLoadFullSizeImage ? " image-button-active" : "")
              }
              style={{ marginBottom: "4px" }}
              onClick={onHdImageLoad}
            >
              <img style={{ height: "18px" }} src={ImageHd} alt="" />
            </div>
          </Tooltip>
        )}
      </div>

      <div id={getCameraId(cameraName)} onMouseMove={onMouseMove}>
        {isFullScreen && (
          <OverlayShowHide isFullScreen={isFullScreen} ref={overlayShowHideRef}>
            <div className="playback-controls-full-screen">
              <div ref={setPlaybackElement} />
            </div>
          </OverlayShowHide>
        )}
      </div>
      {imageNotAvailable && (
        <img
          className="camera-img"
          style={{ position: "absolute", top: "0", right: "0" }}
          src={ImageNotAvailableImage}
          alt=""
        />
      )}
    </div>
  );
});

export default CameraPano;
